import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { EVENEMENT_SESSION_PERDUE } from '../services/api';
import {
  connexion as apiConnexion,
  deconnexion as apiDeconnexion,
  getMoi,
} from '../services/authService';

/**
 * Compte connecté, partagé par toute l'application.
 *
 * Au chargement, on demande au serveur qui porte le cookie de session :
 * `chargement` reste vrai tant qu'il n'a pas répondu, pour ne pas renvoyer
 * à la page de connexion quelqu'un dont la session est en fait valide.
 * Un 401 reçu plus tard, sur n'importe quel appel, fait retomber le compte
 * à null : la garde des routes renvoie alors à la connexion.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [utilisateur, setUtilisateur] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    let vivant = true;
    getMoi()
      .then((res) => {
        if (vivant) setUtilisateur(res.data || null);
      })
      .catch(() => {
        if (vivant) setUtilisateur(null);
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
  }, []);

  useEffect(() => {
    const surPerte = () => setUtilisateur(null);
    window.addEventListener(EVENEMENT_SESSION_PERDUE, surPerte);
    return () => window.removeEventListener(EVENEMENT_SESSION_PERDUE, surPerte);
  }, []);

  const connecter = useCallback(async (identifiant, motDePasse) => {
    const res = await apiConnexion(identifiant, motDePasse);
    setUtilisateur(res.data);
    return res.data;
  }, []);

  const deconnecter = useCallback(async () => {
    try {
      await apiDeconnexion();
    } catch {
      /* la session locale tombe quand même : l'écran repasse à la connexion */
    }
    setUtilisateur(null);
  }, []);

  const valeur = useMemo(
    () => ({ utilisateur, chargement, connecter, deconnecter, setUtilisateur }),
    [utilisateur, chargement, connecter, deconnecter]
  );

  return <AuthContext.Provider value={valeur}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const contexte = useContext(AuthContext);
  if (!contexte) throw new Error('useAuth doit être appelé sous AuthProvider.');
  return contexte;
}
